import apiClient from "@shared/services/apiClient";
import type { LoginComplianceProfile } from "@shared/config/loginComplianceProfile";
import type { PublicComplianceClaim } from "@shared/config/loginTrustClaims";

export type PublicCompliancePosture = {
  profile: LoginComplianceProfile;
  policy_version: string;
  claims: PublicComplianceClaim[];
};

const DEFAULT_CACHE_TTL_MS = 30_000;

const cache = new Map<
  string,
  { expiresAt: number; payload: PublicCompliancePosture | null }
>();
const inFlight = new Map<string, Promise<PublicCompliancePosture | null>>();

function keyForInput(input: { slug?: string; hostname?: string }): string {
  const slug =
    typeof input.slug === "string" ? input.slug.trim().toLowerCase() : "";
  const hostname =
    typeof input.hostname === "string"
      ? input.hostname.trim().toLowerCase()
      : "";
  return `slug:${slug}|hostname:${hostname}`;
}

export async function fetchPublicCompliancePosture(
  input: { slug?: string; hostname?: string },
  options?: { cacheTtlMs?: number },
): Promise<PublicCompliancePosture | null> {
  const key = keyForInput(input);
  const now = Date.now();
  const cacheTtlMs = options?.cacheTtlMs ?? DEFAULT_CACHE_TTL_MS;
  const cached = cache.get(key);

  if (cached && cached.expiresAt > now) {
    return cached.payload;
  }

  const existing = inFlight.get(key);
  if (existing) {
    return existing;
  }

  const request = (async () => {
    try {
      const params = new URLSearchParams();
      if (input.slug) {
        params.set("slug", input.slug.trim());
      }
      if (input.hostname) {
        params.set("hostname", input.hostname.trim());
      }
      const response = await apiClient.get(
        `/auth/public-compliance-posture?${params.toString()}`,
      );
      const payload =
        (response?.data?.data as PublicCompliancePosture | undefined) || null;
      cache.set(key, { expiresAt: Date.now() + cacheTtlMs, payload });
      return payload;
    } catch {
      if (cached) {
        return cached.payload;
      }
      return null;
    } finally {
      inFlight.delete(key);
    }
  })();

  inFlight.set(key, request);
  return request;
}
